import { WebStorageStateStore } from 'oidc-client-ts'
import type { AuthContextProps, AuthProviderProps } from 'react-oidc-context'

/**
 * Authorization Code + PKCE contra o realm do Keycloak, que por sua vez faz o
 * broker do Gov.br. O client é público: não existe secret no bundle.
 */
export const oidcConfig: AuthProviderProps = {
  authority: import.meta.env.VITE_OIDC_AUTHORITY,
  client_id: import.meta.env.VITE_OIDC_CLIENT_ID,
  redirect_uri: import.meta.env.VITE_OIDC_REDIRECT_URI,
  post_logout_redirect_uri: import.meta.env.VITE_OIDC_REDIRECT_URI,
  response_type: 'code',
  scope: 'openid profile email',

  // sessionStorage some ao fechar a aba: um refresh token vazado no
  // localStorage sobrevive a XSS por muito mais tempo.
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),

  automaticSilentRenew: true,

  // Remove ?code=&state= da URL após o retorno do IDP.
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, window.location.pathname)
  },
}

/** Rota da tela de login. Referenciada pelo guarda e pela retomada de rota. */
export const LOGIN_ROUTE = '/login'

/**
 * Primeira tela de quem já tem sessão: a lista de cadastros do requerente.
 *
 * A raiz não é destino — ela só encaminha (ver `useGovBrSession`). Quem entra na
 * aplicação cai no login, e quem já entrou cai na lista, sem página intermediária
 * que ninguém pediu para ver.
 */
export const HOME_ROUTE = '/cadastros'

/**
 * Alias do identity provider Gov.br no realm. Preenchido, o Keycloak pula a
 * própria tela e manda o usuário direto ao Login Único; vazio — o caso de
 * desenvolvimento — o realm oferece os usuários de teste.
 */
const idpHint = import.meta.env.VITE_OIDC_IDP_HINT

/**
 * Normaliza o redirectTo pós-login para um caminho interno.
 *
 * O value chega de query string ou do `state` devolvido pelo IDP, e os dois são
 * escritos por quem monta o link. Sem esta normalização, `?redirect=https://…`
 * transformaria a tela de login numa ponte de phishing com a marca do Gov.br.
 * Na dúvida, raiz.
 */
export function safeRedirectPath(value: unknown): string {
  if (typeof value !== 'string' || value.length === 0) return '/'

  // Precisa começar com uma única barra: `//host` e `/\host` são lidos como
  // autoridade externa pelos navegadores, e `esquema:` sai da aplicação.
  if (!/^\/(?![/\\])/.test(value)) return '/'

  // Voltar à tela de login depois de autenticar é um laço.
  if (value === LOGIN_ROUTE || value.startsWith(`${LOGIN_ROUTE}?`)) return '/'

  return value
}

/** Estado carregado no round-trip do OIDC para retomar a rota pedida. */
export interface LoginState {
  redirectTo: string
}

/**
 * Inicia o fluxo Gov.br guardando o redirectTo no `state` do OIDC.
 *
 * O `redirect_uri` é fixo — está registrado no client do Keycloak —, então o
 * browser sempre volta à raiz; é o `state` que carrega para onde o usuário ia.
 */
export function signinWithGovBr(auth: AuthContextProps, redirectTo: string): Promise<void> {
  const state: LoginState = { redirectTo: safeRedirectPath(redirectTo) }
  return auth.signinRedirect({
    state,
    ...(idpHint ? { extraQueryParams: { kc_idp_hint: idpHint } } : {}),
  })
}
